<template>
  <button
    class="collapse-content w-100 text-dark-3 ps-0"
    @click="toggleCollapse"
    :aria-expanded="!collapsed"
    :aria-controls="componentId"
  >
    <div
      class="w-100 px-0 d-flex flex-row justify-content-between align-items-center mb-space-xs"
    >
      <h3 class="text-left" :class="titleClass">{{ title }}</h3>
      <font-awesome-icon
        class="ms-space-xxs"
        :icon="collapsed ? 'caret-up' : 'caret-down'"
      ></font-awesome-icon>
    </div>
  </button>
  <Transition
    @before-enter="handleCollapseShown"
    @enter="handleCollapseShown"
    name="accordion"
    @before-leave="handleCollapseHidden"
    @leave="handleCollapseHidden"
  >
    <div v-show="collapsed" :aria-hidden="collapsed" :id="componentId">
      <div
        class="form-check mb-space-xxs"
        v-for="option in options"
        :key="option.value"
      >
        <label
          class="form-check-label fs-small d-inline-flex align-items-center"
          :for="`${componentId}-${option.value}`"
          >{{ option.text }}
        </label>
        <input
          class="form-check-input"
          type="checkbox"
          :checked="selected?.has(option.value)"
          @click="handleChecked(option)"
          :id="`${componentId}-${option.value}`"
          :aria-checked="selected?.has(option.value)"
          :aria-label="option.text"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { TitleVariant, VariantOption } from "~/types/types";
export type FilterCollapseOption = {
  value: string;
  text: string;
  description: string;
};

type Props = {
  componentId: string;
  title: string;
  options: FilterCollapseOption[];
  showTooltip?: boolean;
  titleSize?: TitleVariant;
  titleVariant?: VariantOption;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltip: false,
  titleSize: "small",
  titleVariant: "dark-3",
});

/*
 ********************************************
 *  Emits
 ********************************************
 */
const emits = defineEmits<{
  (e: "checked", value: FilterCollapseOption): void;
  (e: "unchecked", value: FilterCollapseOption): void;
  (e: "change", value: FilterCollapseOption): void;
  (e: "input", value: string[]): void;
  (e: "collapse:hidden"): void;
  (e: "collapse:shown"): void;
}>();
/*
 ********************************************
 *  data
 ********************************************
 */
const selected = defineModel<Set<string>>();
const collapsed = ref<boolean>(false);

/*
 ********************************************
 * computed
 ********************************************
 */
const titleClass = computed(() => {
  return `h3-${props.titleSize} fs-${props.titleVariant}`;
});
/*
 ********************************************
 *  methods
 ********************************************
 */
const toggleCollapse = () => {
  collapsed.value = !collapsed.value;
};
const handleChecked = (option: FilterCollapseOption) => {
  if (selected.value?.has(option.value)) {
    selected.value.delete(option.value);
    emits("unchecked", option);
  } else {
    selected.value?.add(option.value);
    emits("checked", option);
  }
  emits("change", option);
};

const initialLoad = ref(false);
const handleCollapseShown = (el: Element) => {
  if (el instanceof HTMLElement) {
    if (collapsed.value) {
      el.style.height = "0px";
      el.style.height = el.scrollHeight + "px";
    } else if (initialLoad.value) {
      el.style.height = el.scrollHeight + "px";
      setTimeout(() => {
        el.style.height = "0px";
      }, 10);
    }
    initialLoad.value = true;
  }
};

const handleCollapseHidden = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    el.style.height = "0";
    collapsed.value ? emits("collapse:shown") : emits("collapse:hidden");
  }
};
</script>

<style scoped>
.collapse-content {
  appearance: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
}
.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.2s ease;
  overflow: hidden;
}
</style>
